export const A4_W = 794;
export const A4_H = 1123;

export type ComponentType =
  | "logo"
  | "companyAddress"
  | "customerAddress"
  | "docTitle"
  | "docNumber"
  | "docDate"
  | "docDueDate"
  | "lineItems"
  | "totals"
  | "textBlock"
  | "notes";

export interface PlacedComponent {
  id: string;
  type: ComponentType;
  x: number;
  y: number;
  w: number;
  h: number;
  props?: Record<string, any>;
}

export interface TemplateStyle {
  fontFamily?: string;
  baseFontSize?: number;
}

export interface LayoutTemplate {
  version: 1;
  components: PlacedComponent[];
  style?: TemplateStyle;
}

/** Beschikbare lettertypes voor het document en per component. */
export const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: "Helvetica / Arial (standaard)", value: "'Helvetica Neue', Arial, sans-serif" },
  { label: "Times / Georgia (serif)", value: "'Times New Roman', Georgia, serif" },
  { label: "Courier (monospace)", value: "'Courier New', Courier, monospace" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
];

export const DEFAULT_FONT_FAMILY = "'Helvetica Neue', Arial, sans-serif";
export const DEFAULT_BASE_FONT_SIZE = 11;

/** Standaardkolommen van het regeloverzicht (lineItems). Volgorde is vast;
 *  zichtbaarheid wordt per template-component bepaald in props.columns. */
export const LINE_ITEM_COLUMNS: { key: string; label: string }[] = [
  { key: "description", label: "Omschrijving" },
  { key: "quantity", label: "Aantal" },
  { key: "unitPrice", label: "Prijs" },
  { key: "vat", label: "BTW %" },
  { key: "vatAmount", label: "BTW bedrag" },
  { key: "lineTotal", label: "Totaal" },
];

/** Default zichtbare kolommen (alles) wanneer props.columns ontbreekt. */
export const DEFAULT_LINE_ITEM_KEYS = LINE_ITEM_COLUMNS.map((c) => c.key);

export interface PaletteItem {
  type: ComponentType;
  label: string;
  defaultW: number;
  defaultH: number;
}

export const PALETTE: PaletteItem[] = [
  { type: "logo", label: "Logo", defaultW: 160, defaultH: 80 },
  { type: "companyAddress", label: "Bedrijfsadres", defaultW: 240, defaultH: 90 },
  { type: "customerAddress", label: "Klant-/leveranciersadres", defaultW: 240, defaultH: 110 },
  { type: "docTitle", label: "Documenttitel", defaultW: 200, defaultH: 40 },
  { type: "docNumber", label: "Documentnummer", defaultW: 160, defaultH: 30 },
  { type: "docDate", label: "Datum", defaultW: 160, defaultH: 30 },
  { type: "docDueDate", label: "Vervaldatum / geldig t/m", defaultW: 200, defaultH: 30 },
  { type: "lineItems", label: "Regeloverzicht", defaultW: 700, defaultH: 300 },
  { type: "totals", label: "Totalen", defaultW: 240, defaultH: 120 },
  { type: "textBlock", label: "Eigen tekstblok", defaultW: 400, defaultH: 80 },
  { type: "notes", label: "Opmerkingen", defaultW: 400, defaultH: 80 },
];

export const labelFor = (type: ComponentType): string =>
  PALETTE.find((p) => p.type === type)?.label || type;

/** Zinnige begin-lay-out (~ de fallback), gebruikt bij "Resetten naar standaard". */
export const DEFAULT_TEMPLATE: LayoutTemplate = {
  version: 1,
  style: { fontFamily: "'Helvetica Neue', Arial, sans-serif", baseFontSize: 11 },
  components: [
    { id: "t-logo", type: "logo", x: 48, y: 40, w: 160, h: 80 },
    { id: "t-company", type: "companyAddress", x: 510, y: 40, w: 240, h: 100 },
    { id: "t-title", type: "docTitle", x: 510, y: 150, w: 240, h: 36 },
    { id: "t-number", type: "docNumber", x: 510, y: 192, w: 160, h: 40 },
    { id: "t-date", type: "docDate", x: 510, y: 236, w: 160, h: 40 },
    { id: "t-due", type: "docDueDate", x: 510, y: 280, w: 200, h: 40 },
    { id: "t-customer", type: "customerAddress", x: 48, y: 200, w: 260, h: 130 },
    { id: "t-lines", type: "lineItems", x: 48, y: 360, w: 700, h: 320 },
    { id: "t-totals", type: "totals", x: 510, y: 700, w: 240, h: 120 },
    {
      id: "t-pay",
      type: "textBlock",
      x: 48,
      y: 1020,
      w: 700,
      h: 60,
      props: { text: "Gelieve binnen 14 dagen te betalen op IBAN ... o.v.v. het factuurnummer.", fontSize: 11, align: "left" },
    },
  ],
};